import React from 'react';
import { Shield, Check, AlertCircle } from 'lucide-react';
import { healthInsurance, cancellationPolicy } from '../data/mock';

const InsuranceSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-stone-50 to-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sage-600 font-medium tracking-wider uppercase text-sm mb-4">
            Insurance & Policies
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-6">
            Health Insurance
            <span className="block text-sage-600">Accepted</span>
          </h2>
          <p className="text-stone-600 text-lg">
            {healthInsurance.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Health Insurance */}
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center">
                <Shield size={28} className="text-sage-600" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-stone-800">Covered Providers</h3>
                <p className="text-sage-600">Claim your massage on health insurance</p>
              </div>
            </div>

            {/* Insurance Provider Logos */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              {healthInsurance.providers.map((provider) => (
                <div
                  key={provider.name}
                  className="bg-stone-50 rounded-2xl p-4 flex flex-col items-center justify-center hover:shadow-md transition-shadow"
                >
                  {provider.logo ? (
                    <div className="w-full h-16 flex items-center justify-center mb-2">
                      <img 
                        src={provider.logo} 
                        alt={provider.name}
                        className="max-h-14 max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mb-2">
                      <Check size={24} className="text-sage-600" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-stone-700 text-center">{provider.name}</span>
                </div>
              ))}
            </div>

            {/* Note */}
            <div className="bg-sage-50 border border-sage-200 rounded-xl p-4">
              <p className="text-sm text-sage-800">
                <strong>How to Claim:</strong> {healthInsurance.note}
              </p>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-warm-50 to-sage-50 rounded-3xl p-8">
              <h3 className="text-2xl font-semibold text-stone-800 mb-4">
                {cancellationPolicy.title}
              </h3>
              
              <p className="text-stone-600 mb-6 leading-relaxed">
                {cancellationPolicy.description}
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  <strong>Please Note:</strong> {cancellationPolicy.warning}
                </p>
              </div>
            </div>

            {/* IMTA Registration */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h4 className="text-lg font-semibold text-stone-800 mb-4">Professional Registrations</h4>
              <p className="text-sm text-stone-500 mb-6">
                Our therapists are registered with recognised professional bodies, ensuring quality and standards.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-5 mb-4">
                <div className="flex items-center gap-4 flex-shrink-0">
                  <img
                    src="/imta-logo.png"
                    alt="Irish Massage Therapists Association (IMTA) member logo"
                    className="w-24 h-24 object-contain"
                    data-testid="imta-logo"
                  />
                  <img
                    src="/nrri-logo.png"
                    alt="National Register of Reflexologists Ireland (NRRI) member logo"
                    className="w-24 h-24 object-contain"
                    data-testid="nrri-logo"
                  />
                </div>
                <div className="space-y-3 w-full">
                  <div className="flex items-center gap-3 bg-stone-50 px-4 py-3 rounded-xl">
                    <Check size={20} className="text-sage-600" />
                    <span className="font-medium text-stone-700">Irish Massage Therapists Association (IMTA)</span>
                  </div>
                  <div className="flex items-center gap-3 bg-stone-50 px-4 py-3 rounded-xl">
                    <Check size={20} className="text-sage-600" />
                    <span className="font-medium text-stone-700">National Register of Reflexologists Ireland (NRRI)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InsuranceSection;
